import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { InboxIcon, InboxSlashIcon } from "modules/icons";
import {
  AdminEditDistributionGroupMutation,
  AdminEditDistributionGroupMutationVariables,
  DistributionGroupFragment,
} from "gql/graphql";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { useMutation } from "@apollo/client";
import ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/admin-edit-distribution-group";
import SyncoTooltip from "modules/_shared/synco-tooltip";

interface IProps {
  distributionGroup?: DistributionGroupFragment;
  userId: string;
}

const AdminToggleNotificationsDistributionGroupButton = ({
  distributionGroup,
  userId,
}: IProps) => {
  const [adminEditDistributionGroupMutationAPI] = useMutation<
    AdminEditDistributionGroupMutation,
    AdminEditDistributionGroupMutationVariables
  >(ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const onClick = useCallback(async () => {
    if (!distributionGroup) return;
    const enableNotifications =
      !distributionGroup.notificationUserIdList.includes(userId);
    await adminEditDistributionGroupMutationAPI({
      variables: {
        id: distributionGroup.id,
        input: {
          notificationUserIdListToAdd: enableNotifications
            ? [userId]
            : undefined,
          notificationUserIdListToRemove: enableNotifications
            ? undefined
            : [userId],
        },
      },
      optimisticResponse: {
        __typename: "Mutation",
        adminEditDistributionGroup: {
          __typename: "DistributionGroup",
          ...distributionGroup,
          notificationUserIdList: enableNotifications
            ? [...distributionGroup.notificationUserIdList, userId]
            : distributionGroup.notificationUserIdList.filter(
                (id) => id !== userId,
              ),
        },
      },
    });
  }, [adminEditDistributionGroupMutationAPI, distributionGroup, userId]);

  const ariaLabel = distributionGroup?.notificationUserIdList.includes(userId)
    ? "notifications on"
    : "notifications off";

  const icon = distributionGroup?.notificationUserIdList.includes(userId) ? (
    <SyncoTooltip label="Group messages sent to Threads Inbox">
      <InboxIcon fill={colors.blue} boxSize={5} />
    </SyncoTooltip>
  ) : (
    <SyncoTooltip label="Group messages not sent to Threads Inbox">
      <InboxSlashIcon fill={colors.grayMain} boxSize={5} />
    </SyncoTooltip>
  );

  if (!distributionGroup) {
    return null;
  }

  return (
    <CircularIconButton
      aria-label={ariaLabel}
      variant="ghost"
      onClick={onClick}
      icon={icon}
    />
  );
};

export default memo(AdminToggleNotificationsDistributionGroupButton);
